import React, { useEffect } from 'react';

/**
 * Loading skeleton for cards
 */
export const CardSkeleton = ({ count = 1, className = '' }) => {
  return (
    <>
      {[...Array(count)]?.map((_, index) => (
        <div
          key={index}
          className={`animate-pulse bg-white rounded-lg shadow-sm border border-gray-200 p-6 ${className}`}
        >
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-gray-200 rounded-lg" />
            <div className="flex-1 space-y-3">
              <div className="h-4 bg-gray-200 rounded w-3/4" />
              <div className="h-3 bg-gray-200 rounded w-1/2" />
              <div className="space-y-2 pt-2">
                <div className="h-3 bg-gray-200 rounded" />
                <div className="h-3 bg-gray-200 rounded w-5/6" />
              </div>
            </div>
          </div>
        </div>
      ))}
    </>
  );
};

/**
 * Loading skeleton for table rows
 */
export const TableRowSkeleton = ({ rows = 5, columns = 4 }) => {
  return (
    <>
      {[...Array(rows)]?.map((_, rowIndex) => (
        <tr key={rowIndex} className="animate-pulse border-b border-gray-200">
          {[...Array(columns)]?.map((_, colIndex) => (
            <td key={colIndex} className="px-6 py-4">
              <div className="h-4 bg-gray-200 rounded w-full" />
            </td>
          ))}
        </tr>
      ))}
    </>
  );
};

/**
 * Loading skeleton for list items
 */
export const ListSkeleton = ({ count = 5, className = '' }) => {
  return (
    <div className={`space-y-3 ${className}`}>
      {[...Array(count)]?.map((_, index) => (
        <div
          key={index}
          className="animate-pulse flex items-center gap-3 p-4 bg-white rounded-lg border border-gray-200"
        >
          <div className="w-10 h-10 bg-gray-200 rounded-full" />
          <div className="flex-1 space-y-2">
            <div className="h-3 bg-gray-200 rounded w-3/4" />
            <div className="h-3 bg-gray-200 rounded w-1/2" />
          </div>
        </div>
      ))}
    </div>
  );
};

/**
 * Loading skeleton for text content
 */
export const TextSkeleton = ({ lines = 3, className = '' }) => {
  return (
    <div className={`animate-pulse space-y-2 ${className}`}>
      {[...Array(lines)]?.map((_, index) => (
        <div
          key={index}
          className={`h-4 bg-gray-200 rounded ${
            index === lines - 1 ? 'w-3/4' : 'w-full'
          }`}
        />
      ))}
    </div>
  );
};

/**
 * Full-screen loading spinner
 */
export const FullScreenLoader = ({ message = 'Loading...' }) => {
  return (
    <div className="fixed inset-0 bg-white bg-opacity-90 flex items-center justify-center z-50">
      <div className="text-center">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
        <p className="mt-4 text-gray-600 font-medium">{message}</p>
      </div>
    </div>
  );
};

/**
 * Inline loading spinner
 */
export const InlineLoader = ({ size = 'md', className = '' }) => {
  const sizeClasses = {
    sm: 'h-4 w-4 border-2',
    md: 'h-6 w-6 border-2',
    lg: 'h-8 w-8 border-3'
  };

  return (
    <div
      className={`inline-block animate-spin rounded-full border-blue-600 border-b-transparent ${sizeClasses?.[size]} ${className}`}
    />
  );
};

/**
 * Button loading state
 */
export const ButtonLoader = ({ loading, children, disabled, ...props }) => {
  return (
    <button {...props} disabled={disabled || loading} className={props?.className}>
      {loading ? (
        <span className="flex items-center gap-2">
          <InlineLoader size="sm" />
          <span>Loading...</span>
        </span>
      ) : (
        children
      )}
    </button>
  );
};

/**
 * Section loading state with message
 */
export const SectionLoader = ({ message = 'Loading...', className = '' }) => {
  return (
    <div className={`flex flex-col items-center justify-center py-12 ${className}`}>
      <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      <p className="mt-4 text-gray-600">{message}</p>
    </div>
  );
};

/**
 * Progressive loading indicator
 */
export const ProgressLoader = ({ progress = 0, message = '', className = '' }) => {
  return (
    <div className={`space-y-2 ${className}`}>
      <div className="w-full bg-gray-200 rounded-full h-2">
        <div
          className="bg-blue-600 h-2 rounded-full transition-all duration-300"
          style={{ width: `${Math.min(100, Math.max(0, progress))}%` }}
        />
      </div>
      {message && (
        <p className="text-sm text-gray-600 text-center">{message}</p>
      )}
    </div>
  );
};

/**
 * Shimmer effect wrapper
 */
export const ShimmerWrapper = ({ children, className = '' }) => {
  return (
    <div className={`relative overflow-hidden ${className}`}>
      {children}
      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent shimmer" />
    </div>
  );
};

/**
 * Dashboard stats skeleton
 */
export const StatsCardSkeleton = ({ count = 4 }) => {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
      {[...Array(count)]?.map((_, index) => (
        <div
          key={index}
          className="animate-pulse bg-white rounded-lg shadow-sm border border-gray-200 p-6"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="h-4 bg-gray-200 rounded w-1/2" />
            <div className="w-8 h-8 bg-gray-200 rounded" />
          </div>
          <div className="h-8 bg-gray-200 rounded w-1/3 mb-2" />
          <div className="h-3 bg-gray-200 rounded w-2/3" />
        </div>
      ))}
    </div>
  );
};

/**
 * Chart skeleton
 */
export const ChartSkeleton = ({ height = '300px', className = '' }) => {
  return (
    <div
      className={`animate-pulse bg-white rounded-lg border border-gray-200 p-6 ${className}`}
      style={{ height }}
    >
      <div className="h-4 bg-gray-200 rounded w-1/3 mb-6" />
      <div className="flex items-end justify-between h-full pb-8 gap-4">
        {[...Array(7)]?.map((_, index) => (
          <div
            key={index}
            className="bg-gray-200 rounded-t w-full"
            style={{ height: `${Math.random() * 60 + 40}%` }}
          />
        ))}
      </div>
    </div>
  );
};

export default {
  CardSkeleton,
  TableRowSkeleton,
  ListSkeleton,
  TextSkeleton,
  FullScreenLoader,
  InlineLoader,
  ButtonLoader,
  SectionLoader,
  ProgressLoader,
  ShimmerWrapper,
  StatsCardSkeleton,
  ChartSkeleton
};
const LoadingState = () => {
  React.useEffect(() => {
    // eslint-disable-next-line no-console
    console.warn('Placeholder: LoadingState is not implemented yet.');
  }, []);
  return (
    <div>
      {/* LoadingState placeholder */}
    </div>
  );
};

export { LoadingState };